import { memo, useCallback, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";
import { chartSpecSchema, type ChartSpec } from "@releve/shared";
import { Chart } from "@/components/ui/highcharts";
import { chartColors } from "@/lib/colors";
import { cn } from "@/lib/utils";
import { CoaProposalCard } from "./CoaProposalCard";

interface BookkeeperMessageProps {
  role: "user" | "assistant";
  content: string;
  /** Optional chart the assistant attached to this answer (SCRUM-174). */
  chartSpec?: ChartSpec | null;
  /**
   * The message id — keys the chart-of-accounts proposal card's once-per-load
   * analytics. Only persisted messages have one (and only they carry a proposal).
   */
  messageId?: string;
  /** `bookkeeper_messages.coa_proposal` — rendered as `CoaProposalCard` (validated there). */
  coaProposal?: unknown;
  /** Phone surface: touch-size controls in the proposal card. */
  touch?: boolean;
}

/**
 * Map a validated {@link ChartSpec} to Highcharts options, reusing the shared
 * wrapper's theming (transparent background, CSS-var axis/tooltip colors) and
 * the Relevé brand `chartColors` for series/slices. Returns `null` when the
 * spec is absent or fails schema validation, so a bad spec never crashes the
 * chat — the message just renders as text.
 */
function useChartOptions(chartSpec?: ChartSpec | null): Highcharts.Options | null {
  return useMemo(() => {
    if (!chartSpec) return null;
    const parsed = chartSpecSchema.safeParse(chartSpec);
    if (!parsed.success) return null;
    const spec = parsed.data;

    const common: Highcharts.Options = {
      chart: { type: spec.type, height: 260 },
      colors: [...chartColors],
      title: { text: spec.title ?? undefined },
      tooltip: { valueSuffix: spec.valueSuffix ?? undefined },
      credits: { enabled: false },
    };

    if (spec.type === "pie") {
      const series = spec.series[0];
      return {
        ...common,
        series: [
          {
            type: "pie",
            id: "series-0",
            name: series?.name,
            data: spec.categories.map((name, i) => ({
              name,
              y: series?.data[i] ?? 0,
              color: chartColors[i % chartColors.length],
            })),
          },
        ],
      };
    }

    return {
      ...common,
      xAxis: { categories: spec.categories },
      yAxis: { title: { text: spec.yAxisLabel ?? undefined } },
      series: spec.series.map((s, i) => ({
        type: spec.type as "line" | "column" | "bar",
        id: `series-${i}`,
        name: s.name,
        data: s.data,
        color: chartColors[i % chartColors.length],
      })),
    };
  }, [chartSpec]);
}

/**
 * Renders one chat message. User turns are a subtle right-aligned bubble;
 * assistant turns are a plain full-width markdown row (matches the channels
 * chat precedent). Internal links (starting with "/") navigate client-side via
 * React Router; external links open in a new tab. HTML in markdown is escaped by
 * react-markdown, so assistant output can't inject markup.
 */
function BookkeeperMessageImpl({
  role,
  content,
  chartSpec,
  messageId,
  coaProposal,
  touch = false,
}: BookkeeperMessageProps) {
  const navigate = useNavigate();
  const chartOptions = useChartOptions(chartSpec);

  // Internal = same-origin absolute path ("/…") but NOT a protocol-relative
  // URL ("//host") which points off-site.
  const isInternal = (href: string | undefined): boolean =>
    !!href && href.startsWith("/") && !href.startsWith("//");

  const handleLink = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, href: string | undefined) => {
      if (isInternal(href)) {
        e.preventDefault();
        navigate(href!);
      }
    },
    [navigate],
  );

  if (role === "user") {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm font-light text-foreground whitespace-pre-wrap break-words">
          {content}
        </div>
      </div>
    );
  }

  const answer = (
    <div
      className={cn(
        // Weights per docs/TYPOGRAPHY.md: body 300, emphasis + headings 500,
        // table headers 500 / table data 400 (the index-grid header row).
        "text-sm font-light leading-relaxed text-foreground",
        "[&_p]:mb-2 [&_p:last-child]:mb-0",
        "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1",
        "[&_strong]:font-medium",
        "[&_code]:rounded [&_code]:border [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[0.85em]",
        "[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2",
        "[&_h1]:text-base [&_h1]:font-medium [&_h2]:text-sm [&_h2]:font-medium [&_h3]:text-sm [&_h3]:font-medium",
        "[&_table]:w-full [&_table]:text-xs [&_thead]:bg-muted",
        "[&_th]:px-2 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-medium [&_th]:whitespace-nowrap",
        "[&_td]:border-t [&_td]:px-2 [&_td]:py-1.5 [&_td]:font-normal [&_td]:tabular-nums",
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          a: ({ href, children, ...props }) => (
            <a
              href={href}
              onClick={(e) => handleLink(e, href)}
              target={isInternal(href) ? undefined : "_blank"}
              rel={isInternal(href) ? undefined : "noopener noreferrer"}
              {...props}
            >
              {children}
            </a>
          ),
          // GFM tables have no intrinsic width limit, and Ré prints whole budget
          // plans as markdown tables — at phone width an unwrapped table pushes
          // the chat layout out horizontally. The scroll container is applied on
          // every viewport, not branched on one: a wide table wants to scroll on
          // desktop too, and the branch would cost more than it saves.
          table: ({ node: _node, children, ...props }) => (
            <div className="my-2 overflow-x-auto rounded-lg border">
              <table {...props}>{children}</table>
            </div>
          ),
        }}
      >
        {content}
      </ReactMarkdown>

      {chartOptions && (
        <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-card p-2">
          <div style={{ minWidth: 280 }}>
            <Chart options={chartOptions} className="h-[260px]" />
          </div>
        </div>
      )}
    </div>
  );

  // The proposal card sits OUTSIDE the markdown wrapper so the prose
  // descendant styles ([&_p], [&_ul], [&_a]) never restyle it.
  if (!coaProposal || !messageId) return answer;
  return (
    <div>
      {answer}
      <CoaProposalCard messageId={messageId} proposal={coaProposal} touch={touch} />
    </div>
  );
}

export const BookkeeperMessage = memo(BookkeeperMessageImpl);
